import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FlatpickrDatePickerComponent } from '../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import {
  CreateMaintenanceRequest, MaintenanceReason, MaintenanceTypeOption, PriorityLevelOption
} from '../../../models/maintenance.model';
import { MaintenanceStatus } from '../../../enums/maintenance-status.enum';
import { PriorityLevel } from '../../../enums/priority-levels.enum';
import { ServiceTypes } from '../../../enums/service-type.enum';
import { MaintenanceService } from '../../../services/maintenance.service';
import { ToastrService } from 'ngx-toastr';
import { MaintenanceSource } from '../../../enums/maintenance-source.enum';
import { startOfDay } from 'date-fns';
import { AssetDropdownItem, AssetSearchDropdownComponent } from '../../asset-search-dropdown/asset-search-dropdown.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-create-maintenance-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, FormsModule, FlatpickrDatePickerComponent, AssetSearchDropdownComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './create-maintenance-modal.component.html',
  styleUrl: './create-maintenance-modal.component.scss'
})
export class CreateMaintenanceModalComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  @ViewChild('dateInput', { static: false }) dateInput!: ElementRef;
  @Input() accountIds: number[] = [];
  @Input() modalTitle: string = '';
  @Input() modalSubtitle: string = '';

  /** When set, the asset is pre-selected and shown as a read-only field instead of the search dropdown. */
  @Input() lockedAssetId?: number;
  @Input() lockedAssetName: string = '';

  form: FormGroup;
  isLoading = false;
  isLoadingReasons = false;
  dueDate = '';
  maintenanceReasons: MaintenanceReason[] = [];
  maintenanceTypes: MaintenanceTypeOption[] = [
    { label: this.transloco.translate('assetShared.createMaintenance.serviceTypes.minor'), value: ServiceTypes.Minor },
    { label: this.transloco.translate('assetShared.createMaintenance.serviceTypes.intermediate'), value: ServiceTypes.Intermediate },
    { label: this.transloco.translate('assetShared.createMaintenance.serviceTypes.major'), value: ServiceTypes.Major }
  ];
  priorityLevels: PriorityLevelOption[] = [
    { label: this.transloco.translate('assetShared.createMaintenance.priorities.low'), value: PriorityLevel.Low },
    { label: this.transloco.translate('assetShared.createMaintenance.priorities.medium'), value: PriorityLevel.Medium },
    { label: this.transloco.translate('assetShared.createMaintenance.priorities.high'), value: PriorityLevel.High }
  ];
  
  // For dropdown button labels
  selectedAsset: string = '';
  selectedMaintenanceType = '';
  selectedPriorityLevel = '';
  selectedMaintenanceReason = '';

  private maintenanceReasonsSubject: Subject<void> = new Subject<void>();

  private maintenanceService = inject(MaintenanceService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);

  constructor(private activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.form = this.fb.group({
      assetId: ['', Validators.required],
      maintenanceType: ['', Validators.required],
      dueDate: ['', Validators.required],
      priorityLevel: ['', Validators.required],
      maintenanceReason: ['', Validators.required],
      taskDescription: ['']
    });
  }

  ngOnInit(): void {
    this.applyLockedAsset();
    this.getMaintenanceReasonsAsync();
  }

  ngOnDestroy(): void {
    this.maintenanceReasonsSubject.next();
    this.maintenanceReasonsSubject.complete();
  }

  get isAssetLocked(): boolean {
    return !!this.lockedAssetId;
  }

  get assetLabel(): string {
    return this.selectedAsset || this.transloco.translate('assetShared.createMaintenance.selectAsset');
  }

  get maintenanceTypeLabel(): string {
    return this.selectedMaintenanceType || this.transloco.translate('assetShared.createMaintenance.selectServiceType');
  }

  get priorityLevelLabel(): string {
    return this.selectedPriorityLevel || this.transloco.translate('assetShared.createMaintenance.selectPriority');
  }

  get maintenanceReasonLabel(): string {
    const selected = this.selectedMaintenanceReason;
    if (!selected) return this.transloco.translate('assetShared.createMaintenance.selectReason');
    return this.maintenanceReasons.find(r => r.name === selected)?.localizedName || selected;
  }

  private applyLockedAsset(): void {
    if (!this.isAssetLocked) return;

    this.selectedAsset = this.lockedAssetName;
    this.form.get('assetId')?.setValue(this.lockedAssetId);
  }

  async getMaintenanceReasonsAsync() {
    this.isLoadingReasons = true;
    
    try {
      this.maintenanceReasonsSubject.next();
      const response = await this.maintenanceService.getMaintenanceReasons(this.maintenanceReasonsSubject);
      if (response.data) {
        this.maintenanceReasons = response.data.map(d => ({
          id: d.id,
          name: d.name,
          localizedName: d.localizedName
        }));
      }

      this.isLoadingReasons = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoadingReasons = false;
        }
      });
    }
  }

  async createMaintenanceAsync(payload: CreateMaintenanceRequest) {
    this.isLoading = true;

    try {
      await this.maintenanceService.createMaintenance(payload);
      this.toastr.success('Maintenance created successfully');

      this.isLoading = false
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  onAssetSelect(asset: AssetDropdownItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedAsset = asset.assetName;
    this.form.get('assetId')?.setValue(asset.assetId);
    this.form.get('assetId')?.markAsTouched();
  }

  onDueDateChange(date: string): void {
    this.dueDate = date;
    this.form.get('dueDate')?.setValue(date);
    this.form.get('dueDate')?.markAsTouched();
  }

  onMaintenanceTypeSelect(type: ServiceTypes, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedMaintenanceType = this.maintenanceTypes.find(t => t.value === type)?.label || '';
    this.form.get('maintenanceType')?.setValue(type);
    this.form.get('maintenanceType')?.markAsTouched();
  }

  onPriorityLevelSelect(priority: PriorityLevel, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedPriorityLevel = this.priorityLevels.find(p => p.value === priority)?.label || '';
    this.form.get('priorityLevel')?.setValue(priority);
    this.form.get('priorityLevel')?.markAsTouched();
  }

  onMaintenanceReasonSelect(reason: MaintenanceReason, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedMaintenanceReason = this.maintenanceReasons.find(r => r.id === reason.id)?.name || '';
    this.form.get('maintenanceReason')?.setValue(reason.id);
    this.form.get('maintenanceReason')?.markAsTouched();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  closeModal(): void {
    this.activeModal.close();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(k => this.form.get(k)?.markAsTouched());
      return;
    }

    const raw = this.form.getRawValue();
    const payload: CreateMaintenanceRequest = {
      assetId: raw.assetId,
      serviceType: raw.maintenanceType,
      dueDate: startOfDay(new Date(raw.dueDate)),
      priorityLevel: raw.priorityLevel,
      taskDescription: raw.taskDescription,
      isAutoTrigger: false,
      maintenanceStatus: MaintenanceStatus.Pending,
      maintenanceSource: MaintenanceSource.System,
      maintenanceReasonId: raw.maintenanceReason,
    };

    await this.createMaintenanceAsync(payload);
    this.activeModal.close();
  }
}